import { blackScholesOption } from './blackScholes'
import { chooseStrike, cycleTradingDays, MIN_IV, skewAdjustedIv, TRADING_DAYS } from './strike'
import { mean, mulberry32, normalDraw, percentile, poissonDraw, stdDev } from './math'
import { getStrategyMeta } from './strategy'
import type {
  CycleRecord,
  ExcessHistogramPoint,
  HistogramPoint,
  PathPoint,
  PercentilePoint,
  SamplePath,
  ScatterPoint,
  SimulationInputs,
  SimulationResult,
  SimulationSummary,
  TerminalOutcome,
  TradeRecord,
} from '../types'

export function validateSimulationInputs(inputs: SimulationInputs): void {
  const numericFields: (keyof SimulationInputs)[] = [
    'initialPrice', 'capital', 'drift', 'realizedVol', 'impliedVol', 'riskFreeRate',
    'cashYield', 'fundingRate', 'dividendYield', 'horizonYears', 'paths', 'dte',
    'targetDelta', 'otmPct', 'fixedCallStrike', 'fixedPutStrike', 'contractMultiplier',
    'notionalMultiple', 'commissionPerContract', 'optionSlippagePct',
    'stockCommissionPerShare', 'stockSlippageBps', 'shortBorrowFee',
    'shortProceedsRebate', 'longRunIv', 'ivMeanReversion', 'stressTriggerReturn',
    'stressIv', 'skewSlope', 'jumpIntensity', 'jumpMean', 'jumpVol', 'seed',
  ]
  for (const field of numericFields) {
    if (typeof inputs[field] !== 'number' || !Number.isFinite(inputs[field])) {
      throw new Error(`${field} must be a finite number.`)
    }
  }
  if (!(inputs.initialPrice > 0)) throw new Error('Initial price must be > 0.')
  if (!(inputs.capital > 0)) throw new Error('Capital must be > 0.')
  if (!(inputs.horizonYears > 0)) throw new Error('Simulation horizon must be > 0.')
  if (!Number.isInteger(inputs.paths) || inputs.paths < 1) throw new Error('Monte Carlo paths must be a positive integer.')
  if (!(inputs.dte > 0)) throw new Error('DTE must be > 0.')
  if (inputs.realizedVol < 0) throw new Error('Realized volatility cannot be negative.')
  if (!(inputs.impliedVol > 0)) throw new Error('Implied volatility must be > 0.')
  if (!(inputs.notionalMultiple > 0)) throw new Error('Notional multiple must be > 0.')
  if (!(inputs.contractMultiplier > 0)) throw new Error('Contract multiplier must be > 0.')
  if (inputs.cashYield < -1 || inputs.fundingRate < -1) throw new Error('Cash and funding rates are invalid.')
  if (inputs.fundingRate < inputs.cashYield) throw new Error('Funding rate should be >= cash yield.')
  if (inputs.commissionPerContract < 0 || inputs.optionSlippagePct < 0 || inputs.stockCommissionPerShare < 0 || inputs.stockSlippageBps < 0) throw new Error('Transaction costs cannot be negative.')
  if (inputs.shortBorrowFee < 0) throw new Error('Short borrow fee cannot be negative.')
  if (inputs.jumpIntensity < 0 || inputs.jumpVol < 0) throw new Error('Jump parameters cannot be negative.')
  if (inputs.ivMeanReversion < 0) throw new Error('IV mean reversion cannot be negative.')
  if (
    inputs.strikeMethod === 'delta' &&
    (!(inputs.targetDelta > 0) || inputs.targetDelta > 0.5)
  ) throw new Error('Target absolute Delta must be in (0, 0.5].')
  if (inputs.strikeMethod === 'otm' && (inputs.otmPct < 0 || inputs.otmPct >= 1)) {
    throw new Error('OTM percentage must be in [0, 100%).')
  }
  if (
    inputs.strikeMethod === 'fixed' &&
    (!(inputs.fixedCallStrike > 0) || !(inputs.fixedPutStrike > 0))
  ) throw new Error('Fixed strike must be > 0.')
}

function optionPayoff(type: 'call' | 'put', spot: number, strike: number): number {
  return type === 'call' ? Math.max(spot - strike, 0) : Math.max(strike - spot, 0)
}

function maxDrawdown(values: number[]): number {
  let peak = values[0] ?? 1
  let worst = 0
  for (const value of values) {
    peak = Math.max(peak, value)
    if (peak > 0) worst = Math.max(worst, (peak - value) / peak)
  }
  return worst
}

function pathSharpe(values: number[], cashYield: number): number | null {
  if (values.length < 3 || values.some((value) => value <= 0)) return null
  const returns: number[] = []
  for (let i = 1; i < values.length; i++) {
    returns.push(values[i] / values[i - 1] - 1)
  }
  const vol = stdDev(returns)
  if (!(vol > 0)) return null
  return ((mean(returns) - cashYield / TRADING_DAYS) / vol) * Math.sqrt(TRADING_DAYS)
}

function roundedShares(targetShares: number, inputs: SimulationInputs): { shares: number; contracts: number } {
  const rawContracts = targetShares / inputs.contractMultiplier
  let contracts = rawContracts
  if (inputs.contractRounding === 'floor') contracts = Math.floor(rawContracts)
  if (inputs.contractRounding === 'nearest') contracts = Math.round(rawContracts)
  contracts = Math.max(0, contracts)
  return {
    shares: contracts * inputs.contractMultiplier,
    contracts,
  }
}

function stockTradeCost(shares: number, price: number, inputs: SimulationInputs): number {
  const quantity = Math.abs(shares)
  return (
    quantity * inputs.stockCommissionPerShare +
    quantity * price * inputs.stockSlippageBps / 10_000
  )
}

function histogram(a: number[], b: number[], bins = 28): HistogramPoint[] {
  const all = [...a, ...b]
  if (!all.length) return []
  const min = all.length >= 100 ? percentile(all, 0.005) : Math.min(...all)
  const max = all.length >= 100 ? percentile(all, 0.995) : Math.max(...all)
  if (!Number.isFinite(min) || !Number.isFinite(max)) return []
  const lo = Math.abs(max - min) < 1e-9 ? min - 0.01 : min
  const hi = Math.abs(max - min) < 1e-9 ? max + 0.01 : max
  const width = (hi - lo) / bins
  const out: HistogramPoint[] = [
    { x: lo - width / 2, label: '< ' + (lo * 100).toFixed(0) + '%', benchmark: 0, strategy: 0, overflow: 'left' },
    ...Array.from({ length: bins }, (_, i) => {
      const x = lo + (i + 0.5) * width
      return { x, label: (x * 100).toFixed(0) + '%', benchmark: 0, strategy: 0 }
    }),
    { x: hi + width / 2, label: '> ' + (hi * 100).toFixed(0) + '%', benchmark: 0, strategy: 0, overflow: 'right' },
  ]
  const add = (value: number, key: 'benchmark' | 'strategy') => {
    if (value < lo) out[0][key]++
    else if (value > hi) out[out.length - 1][key]++
    else {
      const index = Math.min(bins - 1, Math.max(0, Math.floor((value - lo) / width)))
      out[index + 1][key]++
    }
  }
  for (const value of a) add(value, 'benchmark')
  for (const value of b) add(value, 'strategy')
  return out
}

function singleHistogram(values: number[], bins = 28): ExcessHistogramPoint[] {
  if (!values.length) return []
  const min = values.length >= 100 ? percentile(values, 0.005) : Math.min(...values)
  const max = values.length >= 100 ? percentile(values, 0.995) : Math.max(...values)
  if (!Number.isFinite(min) || !Number.isFinite(max)) return []
  const lo = Math.abs(max - min) < 1e-9 ? min - 0.01 : min
  const hi = Math.abs(max - min) < 1e-9 ? max + 0.01 : max
  const width = (hi - lo) / bins
  const out: ExcessHistogramPoint[] = [
    { x: lo - width / 2, label: '< ' + (lo * 100).toFixed(0) + '%', count: 0, overflow: 'left' },
    ...Array.from({ length: bins }, (_, i) => {
      const x = lo + (i + 0.5) * width
      return { x, label: (x * 100).toFixed(0) + '%', count: 0 }
    }),
    { x: hi + width / 2, label: '> ' + (hi * 100).toFixed(0) + '%', count: 0, overflow: 'right' },
  ]
  for (const value of values) {
    if (value < lo) out[0].count++
    else if (value > hi) out[out.length - 1].count++
    else {
      const index = Math.min(bins - 1, Math.max(0, Math.floor((value - lo) / width)))
      out[index + 1].count++
    }
  }
  return out
}

function annualizeTerminalReturn(totalReturn: number, years: number): number | null {
  if (1 + totalReturn <= 0) return null
  return Math.pow(1 + totalReturn, 1 / years) - 1
}

function summarize(outcomes: TerminalOutcome[], inputs: SimulationInputs): SimulationSummary {
  const strategy = outcomes.map((x) => x.strategyReturn)
  const benchmark = outcomes.map((x) => x.benchmarkReturn)
  const strategyStd = stdDev(strategy)
  const excess = outcomes.map((x) => x.excessReturn)
  const excessStd = stdDev(excess)
  const sorted = [...strategy].sort((a, b) => a - b)
  const tailN = Math.max(1, Math.ceil(sorted.length * 0.05))
  const validSharpes = outcomes
    .map((x) => x.pathSharpe)
    .filter((x): x is number => x !== null && Number.isFinite(x))
  const medianStrategyReturn = percentile(strategy, 0.5)
  const medianBenchmarkReturn = percentile(benchmark, 0.5)
  const cashReturn = Math.exp(inputs.cashYield * inputs.horizonYears) - 1

  return {
    meanStrategyReturn: mean(strategy),
    medianStrategyReturn,
    meanBenchmarkReturn: mean(benchmark),
    medianBenchmarkReturn,
    medianStrategyCagr: annualizeTerminalReturn(medianStrategyReturn, inputs.horizonYears),
    medianBenchmarkCagr: annualizeTerminalReturn(medianBenchmarkReturn, inputs.horizonYears),
    meanPathSharpe: validSharpes.length ? mean(validSharpes) : null,
    medianPathSharpe: validSharpes.length ? percentile(validSharpes, 0.5) : null,
    pathSharpeCoverage: validSharpes.length / outcomes.length,
    terminalOutcomeRatio: strategyStd > 0 ? (mean(strategy) - cashReturn) / strategyStd : 0,
    meanMaxDrawdown: mean(outcomes.map((x) => x.maxDrawdown)),
    probabilityLoss: outcomes.filter((x) => x.strategyReturn < 0).length / outcomes.length,
    probabilityOutperform: outcomes.filter((x) => x.strategyReturn > x.benchmarkReturn).length / outcomes.length,
    probabilityEquityBelowZero: outcomes.filter((x) => x.equityBelowZero).length / outcomes.length,
    probabilityLoss50: outcomes.filter((x) => x.strategyReturn <= -0.5).length / outcomes.length,
    probabilityEquityBelow25Pct: outcomes.filter((x) => x.strategyReturn <= -0.75).length / outcomes.length,
    probabilityMarginBreach: outcomes.filter((x) => x.marginBreach).length / outcomes.length,
    p01: percentile(strategy, 0.01),
    p05: percentile(strategy, 0.05),
    p25: percentile(strategy, 0.25),
    p50: percentile(strategy, 0.5),
    p75: percentile(strategy, 0.75),
    p95: percentile(strategy, 0.95),
    worstReturn: sorted[0] ?? 0,
    expectedShortfall05: mean(sorted.slice(0, tailN)),
    averagePremiumIncomePct: mean(outcomes.map((x) => x.premiumIncomePct)),
    averageOptionDebitPct: mean(outcomes.map((x) => x.optionDebitPct)),
    averageNetOptionPnlPct: mean(outcomes.map((x) => x.netOptionPnlPct)),
    averageSettlementRate: mean(outcomes.map((x) =>
      x.optionExpiries ? x.settlements / x.optionExpiries : 0,
    )),
    averageExcessReturn: mean(outcomes.map((x) => x.excessReturn)),
    meanReturnStandardError: outcomes.length > 1 ? strategyStd / Math.sqrt(outcomes.length) : 0,
    meanExcessReturnStandardError: outcomes.length > 1 ? excessStd / Math.sqrt(outcomes.length) : 0,
  }
}

function buildPercentiles(
  days: number[],
  strategySnapshots: number[][],
  benchmarkSnapshots: number[][],
): PercentilePoint[] {
  return days.map((day, index) => {
    const s = strategySnapshots[index]
    const b = benchmarkSnapshots[index]
    return {
      day,
      benchmarkP10: percentile(b, 0.1),
      benchmarkP25: percentile(b, 0.25),
      benchmarkP50: percentile(b, 0.5),
      benchmarkP75: percentile(b, 0.75),
      benchmarkP90: percentile(b, 0.9),
      strategyP10: percentile(s, 0.1),
      strategyP25: percentile(s, 0.25),
      strategyP50: percentile(s, 0.5),
      strategyP75: percentile(s, 0.75),
      strategyP90: percentile(s, 0.9),
    }
  })
}

function evolveIv(currentIv: number, dailyLogReturn: number, inputs: SimulationInputs): number {
  if (inputs.ivModel === 'constant') return inputs.impliedVol
  const reverted = currentIv + inputs.ivMeanReversion * (inputs.longRunIv - currentIv) / TRADING_DAYS
  if (inputs.ivModel === 'stress_regime' && dailyLogReturn <= inputs.stressTriggerReturn) {
    return Math.max(MIN_IV, inputs.stressIv, reverted)
  }
  return Math.max(MIN_IV, reverted)
}

function growCash(cash: number, inputs: SimulationInputs): { cash: number; rate: number } {
  const rate = cash >= 0 ? inputs.cashYield : inputs.fundingRate
  return { cash: cash * Math.exp(rate / TRADING_DAYS), rate }
}

/**
 * Short-sale proceeds are assumed economically segregated: replace whichever
 * generic rate `growCash` just applied to this notional (cashYield or, once the
 * account has gone into deficit, fundingRate) with the configured short-proceeds
 * rebate, then deduct borrow fees. `appliedRate` must be the same rate `growCash`
 * used this step, or the offset silently mismatches whenever total cash is negative.
 */
function applyShortEconomics(
  cash: number,
  shares: number,
  price: number,
  inputs: SimulationInputs,
  appliedRate: number,
): number {
  if (shares >= 0) return cash
  const shortNotional = Math.abs(shares) * price
  return cash + shortNotional * (inputs.shortProceedsRebate - appliedRate - inputs.shortBorrowFee) / TRADING_DAYS
}

function pickRepresentativeSamples(
  candidates: { sample: SamplePath; terminalReturn: number }[],
  allReturns: number[],
): SamplePath[] {
  if (!candidates.length) return []
  const targets: Array<[string, number]> = [
    ['Worst retained', Math.min(...allReturns)],
    ['Near P05', percentile(allReturns, 0.05)],
    ['Near P25', percentile(allReturns, 0.25)],
    ['Near P50', percentile(allReturns, 0.5)],
    ['Near P75', percentile(allReturns, 0.75)],
    ['Near P95', percentile(allReturns, 0.95)],
    ['Best retained', Math.max(...allReturns)],
  ]
  const used = new Set<number>()
  const selected: SamplePath[] = []
  for (const [label, target] of targets) {
    const choice = [...candidates]
      .filter((x) => !used.has(x.sample.id))
      .sort((a, b) => Math.abs(a.terminalReturn - target) - Math.abs(b.terminalReturn - target))[0]
    if (!choice) continue
    used.add(choice.sample.id)
    selected.push({ ...choice.sample, percentileLabel: label })
  }
  for (const candidate of candidates) {
    if (selected.length >= 12) break
    if (!used.has(candidate.sample.id)) selected.push(candidate.sample)
  }
  return selected
}

/**
 * Short-option Monte Carlo engine.
 *
 * Real-world paths use drift + realized volatility (+ optional jumps).
 * Option pricing remains risk-neutral and uses r/q with the path's IV state.
 * This is an economic P&L engine, not a broker-specific margin/liquidation model.
 */
export function runSimulation(
  inputs: SimulationInputs,
  onProgress?: (progress: number) => void,
): SimulationResult {
  return simulate(inputs, onProgress, true)
}

/** Uses the identical path and accounting calculations without retaining charts or ledgers. */
export function runSimulationSummary(
  inputs: SimulationInputs,
  onProgress?: (progress: number) => void,
): SimulationSummary {
  return simulate(inputs, onProgress, false).summary
}

function simulate(
  inputs: SimulationInputs,
  onProgress: ((progress: number) => void) | undefined,
  collectDetails: boolean,
): SimulationResult {
  validateSimulationInputs(inputs)
  const started = performance.now()
  const meta = getStrategyMeta(inputs.strategy)
  const totalDays = Math.max(1, Math.round(inputs.horizonYears * TRADING_DAYS))
  const cycleDays = cycleTradingDays(inputs.dte)
  const cycleYears = cycleDays / TRADING_DAYS
  const dt = 1 / TRADING_DAYS
  const jumpCompensator = inputs.jumpIntensity > 0
    ? inputs.jumpIntensity * (Math.exp(inputs.jumpMean + 0.5 * inputs.jumpVol ** 2) - 1)
    : 0
  const driftStep = (inputs.drift - jumpCompensator - 0.5 * inputs.realizedVol ** 2) * dt
  const volStep = inputs.realizedVol * Math.sqrt(dt)

  const initialRounded = roundedShares(
    (inputs.capital * inputs.notionalMultiple) / inputs.initialPrice,
    inputs,
  )
  const initialOptionShares = initialRounded.shares
  const benchmarkShares = initialOptionShares * meta.benchmarkSign

  const candidateSampleCount = collectDetails ? Math.min(256, inputs.paths) : 0
  const candidatePathSet = new Set(
    Array.from({ length: candidateSampleCount }, (_, i) =>
      candidateSampleCount <= 1
        ? 0
        : Math.round((i * (inputs.paths - 1)) / (candidateSampleCount - 1)),
    ),
  )
  const percentilePathCount = collectDetails ? Math.min(3000, inputs.paths) : 0
  const checkpointCount = collectDetails ? Math.min(37, totalDays + 1) : 0
  const checkpointDays = Array.from({ length: checkpointCount }, (_, i) =>
    Math.round((i * totalDays) / (checkpointCount - 1 || 1)),
  )
  const checkpointSet = new Map(checkpointDays.map((d, i) => [d, i]))
  const strategySnapshots = checkpointDays.map(() => [] as number[])
  const benchmarkSnapshots = checkpointDays.map(() => [] as number[])

  const outcomes: TerminalOutcome[] = []
  const candidateSamples: { sample: SamplePath; terminalReturn: number }[] = []
  const scatter: ScatterPoint[] = []
  const rng = mulberry32(inputs.seed)
  let lastProgress = -1

  type ActiveOptionLeg = {
    optionType: 'call' | 'put'
    position: 'long' | 'short'
    strike: number
    shares: number
    contracts: number
    entryPrice: number
    entryCashFlow: number
    transactionCost: number
  }

  type ActiveCycle = {
    cycle: number
    openDay: number
    shortPremium: number
    optionDebit: number
    entryCashFlow: number
    transactionCost: number
    strike: number
    callStrike: number | null
    putStrike: number | null
    openSpot: number
  }

  const positionSign = (position: 'long' | 'short') => position === 'long' ? 1 : -1

  for (let pathIndex = 0; pathIndex < inputs.paths; pathIndex++) {
    let price = inputs.initialPrice
    let currentIv = inputs.impliedVol
    let optionShares = initialOptionShares
    let contracts = initialRounded.contracts
    let strategyShares = optionShares * meta.underlyingSign
    const initialStrategyStockCost = stockTradeCost(strategyShares, price, inputs)
    const initialBenchmarkStockCost = stockTradeCost(benchmarkShares, price, inputs)
    let strategyCash =
      inputs.capital - strategyShares * price - initialStrategyStockCost
    let benchmarkCash =
      inputs.capital - benchmarkShares * price - initialBenchmarkStockCost
    let optionExpiryDay = cycleDays
    let activeLegs: ActiveOptionLeg[] = []
    let settlements = 0
    let optionExpiries = 0
    let cycles = 0
    let cumulativePremium = 0
    let cumulativeOptionDebit = 0
    let cumulativeOptionEntryCashFlow = 0
    let cumulativeSettlementCashFlow = 0
    let cumulativeSettlementLoss = 0
    let cumulativeOptionCosts = 0
    let equityBelowZero = false
    let marginBreach = false
    let forcedLiquidated = false
    let activeCycle: ActiveCycle | null = null
    // openOptionCycle mutates this variable from a closure. Read through an
    // accessor so TypeScript 5.7 does not retain the initial null state.
    const readActiveCycle = (): ActiveCycle | null => activeCycle

    const wealthHistory: number[] = []
    const samplePoints: PathPoint[] = []
    const trades: TradeRecord[] = []
    const cycleRecords: CycleRecord[] = []
    const keepPath = candidatePathSet.has(pathIndex)

    const optionLegMarkValue = (leg: ActiveOptionLeg, day: number): number => {
      const remainingTradingDays = Math.max(0, optionExpiryDay - day)
      const t = remainingTradingDays / TRADING_DAYS
      return t <= 0
        ? optionPayoff(leg.optionType, price, leg.strike)
        : blackScholesOption(
            leg.optionType,
            price,
            leg.strike,
            t,
            inputs.riskFreeRate,
            inputs.dividendYield,
            skewAdjustedIv(currentIv, price, leg.strike, t, inputs),
          ).price
    }

    const optionMarkContribution = (day: number): number => {
      if (!activeLegs.length) return 0
      return activeLegs.reduce(
        (sum, leg) => sum + positionSign(leg.position) * optionLegMarkValue(leg, day) * leg.shares,
        0,
      )
    }

    const currentStrikes = () => {
      const call = activeLegs.find((leg) => leg.optionType === 'call')?.strike ?? null
      const put = activeLegs.find((leg) => leg.optionType === 'put')?.strike ?? null
      return {
        primary: activeLegs[0]?.strike ?? null,
        call,
        put,
      }
    }

    const netOptionPnl = (day: number) =>
      cumulativeOptionEntryCashFlow +
      cumulativeSettlementCashFlow -
      cumulativeOptionCosts +
      optionMarkContribution(day)

    const economicWealth = (day: number) =>
      strategyCash + strategyShares * price + optionMarkContribution(day)

    const approximateMarginRequirement = (day: number): number => {
      if (inputs.marginMode !== 'reg_t_approx') return 0

      // Research-grade approximation based on Cboe strategy-based maintenance
      // formulas for equity options. It is intentionally conservative for
      // multi-leg structures and is not portfolio margin.
      let required = strategyShares >= 0
        ? 0.25 * strategyShares * price
        : 0.30 * Math.abs(strategyShares) * price

      let coverCalls = Math.max(0, strategyShares)
      let coverPuts = Math.max(0, -strategyShares)

      for (const leg of activeLegs) {
        if (leg.position !== 'short') continue

        if (inputs.strategy === 'cash_secured_put' && leg.optionType === 'put') {
          required += leg.strike * leg.shares
          continue
        }

        let uncoveredShares = leg.shares
        if (leg.optionType === 'call' && coverCalls > 0) {
          const covered = Math.min(uncoveredShares, coverCalls)
          uncoveredShares -= covered
          coverCalls -= covered
        } else if (leg.optionType === 'put' && coverPuts > 0) {
          const covered = Math.min(uncoveredShares, coverPuts)
          uncoveredShares -= covered
          coverPuts -= covered
        }
        if (uncoveredShares <= 0) continue

        const marketValue = optionLegMarkValue(leg, day)
        const outOfMoney = leg.optionType === 'call'
          ? Math.max(leg.strike - price, 0)
          : Math.max(price - leg.strike, 0)
        const twentyPctLessOtm =
          0.20 * price * uncoveredShares - outOfMoney * uncoveredShares
        const minimum = leg.optionType === 'call'
          ? 0.10 * price * uncoveredShares
          : 0.10 * leg.strike * uncoveredShares

        required += marketValue * uncoveredShares + Math.max(twentyPctLessOtm, minimum)
      }
      return Math.max(0, required)
    }

    const liquidateForMargin = (day: number): boolean => {
      if (inputs.marginMode !== 'reg_t_approx' || forcedLiquidated) return false

      const equity = economicWealth(day)
      const requirement = approximateMarginRequirement(day)
      if (requirement <= Math.max(0, equity) + 1e-9) return false

      marginBreach = true
      forcedLiquidated = true

      let optionCloseCashFlow = 0
      let optionCloseCosts = 0
      for (const leg of activeLegs) {
        const value = optionLegMarkValue(leg, day)
        const gross = positionSign(leg.position) * value * leg.shares
        const cost =
          value * leg.shares * inputs.optionSlippagePct +
          leg.contracts * inputs.commissionPerContract
        strategyCash += gross - cost
        cumulativeSettlementCashFlow += gross
        cumulativeOptionCosts += cost
        optionCloseCashFlow += gross
        optionCloseCosts += cost

        if (keepPath) {
          trades.push({
            day,
            cycle: cycles,
            action: leg.optionType === 'call' ? 'MARGIN_CLOSE_CALL' : 'MARGIN_CLOSE_PUT',
            instrument: leg.optionType === 'call' ? 'CALL' : 'PUT',
            position: leg.position,
            quantity: leg.shares,
            contracts: leg.contracts,
            spot: price,
            strike: leg.strike,
            price: value,
            grossCashFlow: gross,
            transactionCost: cost,
            netCashFlow: gross - cost,
            note: 'Forced close after Reg-T approximation breach.',
          })
        }
      }

      const closingCycle = readActiveCycle()
      if (keepPath && closingCycle && activeLegs.length) {
        cycleRecords.push({
          cycle: closingCycle.cycle,
          openDay: closingCycle.openDay,
          closeDay: day,
          premium: closingCycle.shortPremium,
          optionDebit: closingCycle.optionDebit,
          settlementCashFlow: optionCloseCashFlow,
          terminalMark: 0,
          settlementLoss: 0,
          shortOptionMarkLiability: 0,
          transactionCost: closingCycle.transactionCost + optionCloseCosts,
          netOptionPnl:
            closingCycle.entryCashFlow +
            optionCloseCashFlow -
            closingCycle.transactionCost -
            optionCloseCosts,
          strike: closingCycle.strike,
          callStrike: closingCycle.callStrike,
          putStrike: closingCycle.putStrike,
          openSpot: closingCycle.openSpot,
          closeSpot: price,
        })
      }

      activeLegs = []
      activeCycle = null

      if (strategyShares !== 0) {
        const closingShares = -strategyShares
        const gross = strategyShares * price
        const cost = stockTradeCost(closingShares, price, inputs)
        strategyCash += gross - cost
        if (keepPath) {
          trades.push({
            day,
            cycle: cycles,
            action: 'MARGIN_CLOSE_STOCK',
            instrument: 'STOCK',
            quantity: Math.abs(strategyShares),
            spot: price,
            price,
            grossCashFlow: gross,
            transactionCost: cost,
            netCashFlow: gross - cost,
            note: 'Close stock leg after Reg-T approximation breach.',
          })
        }
      }

      strategyShares = 0
      optionShares = 0
      contracts = 0
      return true
    }

    const resizeForNextCycle = (day: number) => {
      if (forcedLiquidated) return

      if (inputs.sizingMode === 'fixed_units') {
        optionShares = initialOptionShares
        contracts = initialRounded.contracts
      } else if (day !== 0) {
        const equity = economicWealth(day)
        const next = equity > 0
          ? roundedShares((equity * inputs.notionalMultiple) / price, inputs)
          : inputs.insolvencyMode === 'stop_new_risk'
            ? { shares: 0, contracts: 0 }
            : { shares: optionShares, contracts }
        optionShares = next.shares
        contracts = next.contracts
      }

      // Physical assignment/exercise can change the stock position at expiry.
      // Restore the requested strategy stock leg before opening the next cycle.
      const targetStrategyShares = optionShares * meta.underlyingSign
      const deltaShares = targetStrategyShares - strategyShares
      if (deltaShares !== 0) {
        const transactionCost = stockTradeCost(deltaShares, price, inputs)
        strategyCash -= deltaShares * price + transactionCost
        if (keepPath) {
          trades.push({
            day,
            cycle: cycles + 1,
            action: 'RESIZE_STOCK',
            instrument: 'STOCK',
            quantity: Math.abs(deltaShares),
            spot: price,
            price,
            grossCashFlow: -deltaShares * price,
            transactionCost,
            netCashFlow: -deltaShares * price - transactionCost,
            note: inputs.settlementStyle === 'physical_expiry'
              ? 'Restore strategy stock leg after physical expiry.'
              : 'Rebalance stock leg to target option notional.',
          })
        }
      }
      strategyShares = targetStrategyShares
    }

    if (keepPath && strategyShares !== 0) {
      trades.push({
        day: 0,
        cycle: 0,
        action: strategyShares > 0 ? 'BUY_STOCK' : 'SHORT_STOCK',
        instrument: 'STOCK',
        quantity: Math.abs(strategyShares),
        spot: price,
        price,
        grossCashFlow: -strategyShares * price,
        transactionCost: initialStrategyStockCost,
        netCashFlow: -strategyShares * price - initialStrategyStockCost,
        note: strategyShares > 0 ? 'Establish long-stock leg.' : 'Establish short-stock leg.',
      })
    }

    const openOptionCycle = (day: number) => {
      if (forcedLiquidated) return
      resizeForNextCycle(day)
      if (optionShares <= 0 || !meta.optionLegs.length) {
        activeLegs = []
        activeCycle = null
        return
      }

      optionExpiryDay = day + cycleDays
      const nextLegs: ActiveOptionLeg[] = []
      let cycleShortPremium = 0
      let cycleOptionDebit = 0
      let cycleEntryCashFlow = 0
      let cycleCosts = 0

      for (const definition of meta.optionLegs) {
        const strike = chooseStrike(
          definition.optionType,
          price,
          inputs,
          cycleYears,
          currentIv,
        )
        if (inputs.strategy === 'cash_secured_put') {
          const maxContractsRaw =
            Math.max(0, strategyCash) / (strike * inputs.contractMultiplier)
          const collateralContracts =
            inputs.contractRounding === 'fractional'
              ? maxContractsRaw
              : Math.floor(maxContractsRaw)
          contracts = Math.max(0, Math.min(contracts, collateralContracts))
          optionShares = contracts * inputs.contractMultiplier
          if (optionShares <= 0) break
        }

        const entryIv = skewAdjustedIv(currentIv, price, strike, cycleYears, inputs)
        const entryPrice = blackScholesOption(
          definition.optionType,
          price,
          strike,
          cycleYears,
          inputs.riskFreeRate,
          inputs.dividendYield,
          entryIv,
        ).price
        const grossPremium = entryPrice * optionShares
        const rawEntryCashFlow = -positionSign(definition.position) * grossPremium
        const slippage = grossPremium * inputs.optionSlippagePct
        const commission = contracts * inputs.commissionPerContract
        const cost = slippage + commission

        strategyCash += rawEntryCashFlow - cost
        cumulativeOptionEntryCashFlow += rawEntryCashFlow
        cumulativeOptionCosts += cost
        cycleEntryCashFlow += rawEntryCashFlow
        cycleCosts += cost

        if (definition.position === 'short') {
          cumulativePremium += grossPremium
          cycleShortPremium += grossPremium
        } else {
          cumulativeOptionDebit += grossPremium
          cycleOptionDebit += grossPremium
        }

        const leg: ActiveOptionLeg = {
          optionType: definition.optionType,
          position: definition.position,
          strike,
          shares: optionShares,
          contracts,
          entryPrice,
          entryCashFlow: rawEntryCashFlow,
          transactionCost: cost,
        }
        nextLegs.push(leg)

        if (keepPath) {
          const isCall = definition.optionType === 'call'
          const isLong = definition.position === 'long'
          trades.push({
            day,
            cycle: cycles + 1,
            action: isLong
              ? isCall ? 'BUY_CALL' : 'BUY_PUT'
              : isCall ? 'SELL_CALL' : 'SELL_PUT',
            instrument: isCall ? 'CALL' : 'PUT',
            position: definition.position,
            quantity: optionShares,
            contracts,
            spot: price,
            strike,
            price: entryPrice,
            grossCashFlow: rawEntryCashFlow,
            transactionCost: cost,
            netCashFlow: rawEntryCashFlow - cost,
            note: definition.label + ' · ' + inputs.dte + ' DTE @ IV ' + (entryIv * 100).toFixed(1) + '%',
          })
        }
      }

      if (!nextLegs.length) {
        activeLegs = []
        activeCycle = null
        return
      }

      activeLegs = nextLegs
      cycles++
      const strikes = currentStrikes()
      activeCycle = {
        cycle: cycles,
        openDay: day,
        shortPremium: cycleShortPremium,
        optionDebit: cycleOptionDebit,
        entryCashFlow: cycleEntryCashFlow,
        transactionCost: cycleCosts,
        strike: strikes.primary ?? price,
        callStrike: strikes.call,
        putStrike: strikes.put,
        openSpot: price,
      }
    }

    openOptionCycle(0)
    liquidateForMargin(0)

    const markWealth = (day: number) => {
      const strategyWealth = economicWealth(day)
      const benchmarkWealth = benchmarkCash + benchmarkShares * price
      equityBelowZero ||= strategyWealth <= 0
      wealthHistory.push(strategyWealth)

      if (pathIndex < percentilePathCount) {
        const checkpoint = checkpointSet.get(day)
        if (checkpoint !== undefined) {
          strategySnapshots[checkpoint].push(strategyWealth / inputs.capital)
          benchmarkSnapshots[checkpoint].push(benchmarkWealth / inputs.capital)
        }
      }
      return { strategyWealth, benchmarkWealth }
    }

    const initialMarks = markWealth(0)
    if (keepPath) {
      const strikes = currentStrikes()
      const firstLeg = meta.optionLegs[0]
      samplePoints.push({
        day: 0,
        price,
        impliedVol: currentIv,
        strategyWealth: initialMarks.strategyWealth,
        benchmarkWealth: initialMarks.benchmarkWealth,
        strike: strikes.primary,
        callStrike: strikes.call,
        putStrike: strikes.put,
        cumulativePremium,
        cumulativeOptionDebit,
        cumulativeSettlementCashFlow,
        cumulativeSettlementLoss,
        netOptionPnl: netOptionPnl(0),
        event: firstLeg
          ? firstLeg.position === 'long'
            ? firstLeg.optionType === 'call' ? 'buy-call' : 'buy-put'
            : firstLeg.optionType === 'call' ? 'sell-call' : 'sell-put'
          : undefined,
      })
    }

    for (let day = 1; day <= totalDays; day++) {
      let jumpLog = 0
      if (inputs.jumpIntensity > 0) {
        const jumpCount = poissonDraw(rng, inputs.jumpIntensity / TRADING_DAYS)
        for (let jumpIndex = 0; jumpIndex < jumpCount; jumpIndex++) {
          jumpLog += inputs.jumpMean + inputs.jumpVol * normalDraw(rng)
        }
      }
      const dailyLogReturn = driftStep + volStep * normalDraw(rng) + jumpLog
      price *= Math.exp(dailyLogReturn)
      currentIv = evolveIv(currentIv, dailyLogReturn, inputs)

      const strategyGrowth = growCash(strategyCash, inputs)
      const benchmarkGrowth = growCash(benchmarkCash, inputs)
      strategyCash = applyShortEconomics(strategyGrowth.cash, strategyShares, price, inputs, strategyGrowth.rate)
      benchmarkCash = applyShortEconomics(benchmarkGrowth.cash, benchmarkShares, price, inputs, benchmarkGrowth.rate)

      strategyCash += (strategyShares * price * inputs.dividendYield) / TRADING_DAYS
      benchmarkCash += (benchmarkShares * price * inputs.dividendYield) / TRADING_DAYS

      let event: PathPoint['event']
      liquidateForMargin(day)

      if (activeLegs.length && day === optionExpiryDay) {
        let settlementCashFlow = 0
        let shortSettlementLoss = 0
        let anyItm = false
        let firstItmType: 'call' | 'put' | null = null

        for (const leg of activeLegs) {
          const payoffPerShare = optionPayoff(leg.optionType, price, leg.strike)
          const signedCashFlow =
            positionSign(leg.position) * payoffPerShare * leg.shares
          settlementCashFlow += signedCashFlow
          optionExpiries++

          if (payoffPerShare > 0) {
            settlements++
            anyItm = true
            firstItmType ??= leg.optionType
            if (leg.position === 'short') {
              shortSettlementLoss += payoffPerShare * leg.shares
            }

            if (inputs.settlementStyle === 'physical_expiry') {
              const shareDelta = leg.optionType === 'call'
                ? (leg.position === 'long' ? leg.shares : -leg.shares)
                : (leg.position === 'long' ? -leg.shares : leg.shares)
              const deliveryCashFlow = -shareDelta * leg.strike
              strategyShares += shareDelta
              strategyCash += deliveryCashFlow

              if (keepPath) {
                const action =
                  leg.optionType === 'call'
                    ? leg.position === 'short' ? 'CALL_ASSIGNED' : 'CALL_EXERCISED'
                    : leg.position === 'short' ? 'PUT_ASSIGNED' : 'PUT_EXERCISED'
                trades.push({
                  day,
                  cycle: cycles,
                  action,
                  instrument: leg.optionType === 'call' ? 'CALL' : 'PUT',
                  position: leg.position,
                  quantity: leg.shares,
                  contracts: leg.contracts,
                  spot: price,
                  strike: leg.strike,
                  price: leg.strike,
                  grossCashFlow: deliveryCashFlow,
                  transactionCost: 0,
                  netCashFlow: deliveryCashFlow,
                  note:
                    'Physical expiry delivery at strike; option P&L equivalent = ' +
                    signedCashFlow.toFixed(2) + '.',
                })
              }
            }
          }

          if (keepPath && (payoffPerShare <= 0 || inputs.settlementStyle === 'cash')) {
            const isCall = leg.optionType === 'call'
            trades.push({
              day,
              cycle: cycles,
              action: payoffPerShare > 0
                ? isCall ? 'CALL_SETTLEMENT' : 'PUT_SETTLEMENT'
                : isCall ? 'CALL_EXPIRED' : 'PUT_EXPIRED',
              instrument: isCall ? 'CALL' : 'PUT',
              position: leg.position,
              quantity: leg.shares,
              contracts: leg.contracts,
              spot: price,
              strike: leg.strike,
              price: payoffPerShare,
              grossCashFlow: signedCashFlow,
              transactionCost: 0,
              netCashFlow: signedCashFlow,
              note: payoffPerShare > 0
                ? leg.position + ' option expiry cash settlement.'
                : leg.position + ' option expired OTM.',
            })
          }
        }

        if (inputs.settlementStyle === 'cash') {
          strategyCash += settlementCashFlow
        }
        cumulativeSettlementCashFlow += settlementCashFlow
        cumulativeSettlementLoss += shortSettlementLoss

        const closingCycle = readActiveCycle()
        if (keepPath && closingCycle) {
          cycleRecords.push({
            cycle: closingCycle.cycle,
            openDay: closingCycle.openDay,
            closeDay: day,
            premium: closingCycle.shortPremium,
            optionDebit: closingCycle.optionDebit,
            settlementCashFlow,
            terminalMark: 0,
            settlementLoss: shortSettlementLoss,
            shortOptionMarkLiability: 0,
            transactionCost: closingCycle.transactionCost,
            netOptionPnl:
              closingCycle.entryCashFlow +
              settlementCashFlow -
              closingCycle.transactionCost,
            strike: closingCycle.strike,
            callStrike: closingCycle.callStrike,
            putStrike: closingCycle.putStrike,
            openSpot: closingCycle.openSpot,
            closeSpot: price,
          })
        }

        activeLegs = []
        activeCycle = null
        event = anyItm
          ? firstItmType === 'call' ? 'settled-call' : 'settled-put'
          : 'expired-otm'

        if (day < totalDays) {
          openOptionCycle(day)
          liquidateForMargin(day)
        }
      }

      const marks = markWealth(day)
      if (keepPath) {
        const strikes = currentStrikes()
        samplePoints.push({
          day,
          price,
          impliedVol: currentIv,
          strategyWealth: marks.strategyWealth,
          benchmarkWealth: marks.benchmarkWealth,
          strike: strikes.primary,
          callStrike: strikes.call,
          putStrike: strikes.put,
          cumulativePremium,
          cumulativeOptionDebit,
          cumulativeSettlementCashFlow,
          cumulativeSettlementLoss,
          netOptionPnl: netOptionPnl(day),
          event,
        })
      }
    }

    const finalStrategyWealth = wealthHistory[wealthHistory.length - 1]
    const finalBenchmarkWealth = benchmarkCash + benchmarkShares * price
    const terminalOptionMark = optionMarkContribution(totalDays)

    const terminalCycle = readActiveCycle()
    if (keepPath && terminalCycle) {
      cycleRecords.push({
        cycle: terminalCycle.cycle,
        openDay: terminalCycle.openDay,
        closeDay: totalDays,
        premium: terminalCycle.shortPremium,
        optionDebit: terminalCycle.optionDebit,
        settlementCashFlow: 0,
        terminalMark: terminalOptionMark,
        settlementLoss: 0,
        shortOptionMarkLiability: activeLegs
          .filter((leg) => leg.position === 'short')
          .reduce((sum, leg) => {
            const remainingTradingDays = Math.max(0, optionExpiryDay - totalDays)
            const t = remainingTradingDays / TRADING_DAYS
            const mark = t <= 0
              ? optionPayoff(leg.optionType, price, leg.strike)
              : blackScholesOption(
                  leg.optionType,
                  price,
                  leg.strike,
                  t,
                  inputs.riskFreeRate,
                  inputs.dividendYield,
                  skewAdjustedIv(currentIv, price, leg.strike, t, inputs),
                ).price
            return sum + mark * leg.shares
          }, 0),
        transactionCost: terminalCycle.transactionCost,
        netOptionPnl:
          terminalCycle.entryCashFlow +
          terminalOptionMark -
          terminalCycle.transactionCost,
        strike: terminalCycle.strike,
        callStrike: terminalCycle.callStrike,
        putStrike: terminalCycle.putStrike,
        openSpot: terminalCycle.openSpot,
        closeSpot: price,
      })
    }

    const outcome: TerminalOutcome = {
      benchmarkReturn: finalBenchmarkWealth / inputs.capital - 1,
      strategyReturn: finalStrategyWealth / inputs.capital - 1,
      excessReturn: (finalStrategyWealth - finalBenchmarkWealth) / inputs.capital,
      underlyingReturn: price / inputs.initialPrice - 1,
      maxDrawdown: maxDrawdown(wealthHistory),
      premiumIncomePct: cumulativePremium / inputs.capital,
      optionDebitPct: cumulativeOptionDebit / inputs.capital,
      netOptionPnlPct: netOptionPnl(totalDays) / inputs.capital,
      settlements,
      optionExpiries,
      cycles,
      equityBelowZero,
      marginBreach,
      pathSharpe: pathSharpe(wealthHistory, inputs.cashYield),
    }
    outcomes.push(outcome)

    if (keepPath) {
      candidateSamples.push({
        sample: { id: pathIndex + 1, points: samplePoints, trades, cycles: cycleRecords },
        terminalReturn: outcome.strategyReturn,
      })
    }

    if (collectDetails && pathIndex % Math.max(1, Math.floor(inputs.paths / 900)) === 0 && scatter.length < 900) {
      scatter.push({ benchmark: outcome.benchmarkReturn, strategy: outcome.strategyReturn })
    }

    const progress = Math.floor(((pathIndex + 1) / inputs.paths) * 100)
    if (progress !== lastProgress && (progress % 2 === 0 || progress === 100)) {
      lastProgress = progress
      onProgress?.(progress)
    }
  }

  const benchmarkReturns = collectDetails ? outcomes.map((x) => x.benchmarkReturn) : []
  const strategyReturns = collectDetails ? outcomes.map((x) => x.strategyReturn) : []
  const excess = collectDetails ? outcomes.map((x) => x.excessReturn) : []

  return {
    inputs,
    summary: summarize(outcomes, inputs),
    samples: pickRepresentativeSamples(candidateSamples, strategyReturns),
    percentiles: buildPercentiles(checkpointDays, strategySnapshots, benchmarkSnapshots),
    histogram: histogram(benchmarkReturns, strategyReturns),
    excessHistogram: singleHistogram(excess),
    scatter,
    elapsedMs: performance.now() - started,
  }
}
